import { FormControlLabel, Radio, styled, SvgIcon } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { forwardRef } from 'react';
import { v4 as uuid } from 'uuid';
import Text from './Text';

const CustomRadio = styled(Radio)(({ theme, error }) => {
    return {
        padding: theme.spacing(2),
        '&.Mui-focusVisible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '-4px',
        },
        '&:hover': {
            background: 'transparent',
            '&:not(.Mui-checked) circle': {
                ...(!error && {
                    stroke: theme.palette.ensightful.neutral[700],
                }),
            },
        },
        '&:active': {
            '&:not(.Mui-checked) circle': {
                ...(!error && {
                    stroke: theme.palette.ensightful.neutral[800],
                }),
            },
        },
        '& > input': {
            width: 40,
            height: 40,
        },
    };
});

const CustomFormControlLabel = styled(FormControlLabel)(({ theme }) => {
    return {
        gap: theme.spacing(1),
        margin: 0,
    };
});

const getIcon = (theme, checked, error, disabled) => {
    if (checked) {
        return disabled ? (
            <SvgIcon>
                <circle cx={'12'} cy={'12'} r={'12'} fill={theme.palette.ensightful.neutral[300]} />
                <circle cx={'12'} cy={'12'} r={'6'} fill={theme.palette.ensightful.neutral[500]} />
            </SvgIcon>
        ) : (
            <SvgIcon>
                <circle cx={'12'} cy={'12'} r={'12'} fill={theme.palette.ensightful.primary[500]} />
                <circle cx={'12'} cy={'12'} r={'6'} fill={theme.palette.ensightful.shades[0]} />
            </SvgIcon>
        );
    } else {
        if (disabled) {
            return (
                <SvgIcon>
                    <circle
                        cx={'12'}
                        cy={'12'}
                        r={'11.5'}
                        fill={theme.palette.ensightful.neutral[300]}
                        stroke={theme.palette.ensightful.neutral[400]}
                    />
                </SvgIcon>
            );
        } else {
            return error ? (
                <SvgIcon>
                    <rect width={'24'} height={'24'} fill={'transparent'} />
                    <circle
                        cx={'12'}
                        cy={'12'}
                        r={'11.5'}
                        fill={theme.palette.ensightful.error[50]}
                        stroke={theme.palette.ensightful.error[700]}
                    />
                </SvgIcon>
            ) : (
                <SvgIcon>
                    <circle
                        cx={'12'}
                        cy={'12'}
                        r={'11.5'}
                        fill={theme.palette.ensightful.shades[0]}
                        stroke={theme.palette.ensightful.neutral[600]}
                    />
                </SvgIcon>
            );
        }
    }
};

export default forwardRef(({ id, disabled, error, label, labelPlacement, ...rest }, ref) => {
    const theme = useTheme();

    const controlId = id ?? uuid();

    const ControlComponent = (
        <CustomRadio
            aria-disabled={undefined}
            ref={ref}
            id={controlId}
            checkedIcon={getIcon(theme, true, error, disabled)}
            disabled={disabled}
            error={error}
            icon={getIcon(theme, false, error, disabled)}
            {...(label ? null : rest)}
        />
    );

    let labelColor = 'default';
    if (error) labelColor = 'error';
    if (disabled) labelColor = 'disabled';

    return label ? (
        <CustomFormControlLabel
            htmlFor={controlId}
            control={ControlComponent}
            disableTypography
            label={
                typeof label === 'string' ? (
                    <Text
                        color={labelColor}
                        component={'span'}
                        variant={'p2'}
                        align={labelPlacement === 'bottom' ? 'center' : 'left'}
                    >
                        {label}
                    </Text>
                ) : (
                    label
                )
            }
            labelPlacement={labelPlacement}
            {...(label ? rest : null)}
        />
    ) : (
        ControlComponent
    );
});
